import { useState, useEffect, useRef } from "react";
import {
    Form,
    Link,
    useActionData,
    useRouteLoaderData,
    useSubmit,
} from "react-router-dom";
import Cleave from "cleave.js/react";

import { states, cityStates } from "src/components/common/cityState";
import { Organization } from "./types";
import { useModalStore } from "../main/modalStore";

export default function OrganizationForm({
    keyword
}: {
    keyword: "Add" | "Edit";
}) {
    const actionData = useActionData();
    const loaderOrg = useRouteLoaderData("organization")?.organization ?? {
        id: "",
        name: "",
        website: "",
        phoneNumber: "",
        extension: "",
        fax: "",

        billingStreet: "",
        billingCity: "",
        billingState: "",
        billingZipCode: "",
        overseaAdress: "", 

        shippingStreet: "",
        shippingCity: "",
        shippingState: "",
        shippingZipCode: "",
        overseaShippingAdress: "", 

        background: ""
    };
    const [formValues, setFormValues] = useState(loaderOrg);
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormValues((prev:Organization)=> ({
            ...prev,
            [name]: value
        }));
    };
    const submit = useSubmit();
    const formRef = useRef(null);
    useEffect(() => {
        if(actionData?.duplicate){
            useModalStore.getState().showModal({
                title: "Warning",
                message: "Potential Duplicates:",
                organizations: actionData.data
            });
            useModalStore.getState().modal!.onConfirm = async () => {
                const newForm = new FormData(formRef.current!);
                newForm.append("force", "1");
                submit(newForm, { method: "post" });
            }
        }
    }, [actionData, submit])
    return (
        <Form method="post" ref = {formRef}>
            <input type="hidden" name="action" value={keyword} />
            <input type="hidden" name="id" value={formValues.id ?? ""} />
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">General Information</h4>
                    <div className="d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap" htmlFor = "name">
                            Organization Name:
                        </label>
                        <input
                            className = "form-control flex-grow-1 ms-3"
                            required
                            autoComplete = "organizationName"
                            id = "name"
                            name = "name"
                            value = {formValues.name ?? ""}
                            onChange = {handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="website">Website:</label>
                        <input
                            type="url"
                            className="form-control flex-grow-1 ms-3"
                            id="website"
                            name="website"
                            value={formValues.website ?? ""}
                            onChange = {handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="phoneNumber">Business Phone:</label>
                        <Cleave
                            className = {`form-control flex-grow-1 ms-3 ${
                                formValues.phoneNumber && formValues.phoneNumber.replace(/\D/g, "").length > 0 &&
                                formValues.phoneNumber.replace(/\D/g, "").length < 10
                                    ? "is-invalid"
                                    : ""
                            }`}
                            id = "phoneNumber"
                            name = "phoneNumber"
                            value = {formValues.phoneNumber ?? ""}
                            onChange = {(e) =>
                                setFormValues({
                                    ...formValues,
                                    phoneNumber: e.target.value
                                })
                            }
                            options = {{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="extension">Extension:</label>
                        <input
                            className = "form-control flex-grow-1 ms-3"
                            id="extension"
                            name="extension"
                            value={formValues.extension ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="fax">Fax:</label>
                        <Cleave
                            className = {`form-control flex-grow-1 ms-3 ${
                                formValues.fax && formValues.fax.replace(/\D/g, "").length > 0 &&
                                formValues.fax.replace(/\D/g, "").length < 10
                                    ? "is-invalid"
                                    : ""
                            }`}
                            id="fax"
                            name="fax"
                            value={formValues.fax ?? ""}
                            onChange={(e) =>
                                setFormValues({
                                    ...formValues,
                                    fax: e.target.value
                                })
                            }
                            options={{
                                blocks: [3, 3, 4],
                                delimiter: "-",
                                numericOnly: true
                            }}
                        />
                    </div>
                </div>
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">Billing Address</h4>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="billingStreet">Street:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="billingStreet"
                            name="billingStreet"
                            value={formValues.billingStreet ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="billingCity">City:</label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id="billingCity"
                            name="billingCity"
                            value={formValues.billingCity ?? ""}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {formValues.billingState &&
                                cityStates[formValues.billingState as keyof typeof cityStates]?.map(
                                    (s, i) => <option key={i}>{s}</option>
                                )}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="billingState">State:</label>
                        <select
                            className = "form-control flex-grow-1 ms-3"
                            id="billingState"
                            name = "billingState"
                            value = {formValues.billingState ?? ""}
                            onChange = {handleChange}
                        >
                            <option value="">Select</option>
                            {states.map((s, i) => (
                                <option key={i}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="billingZipCode">Zip Code:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="billingZipCode"
                            name="billingZipCode"
                            value={formValues.billingZipCode ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="overseaBillingAddress">Oversea Address:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="overseaBillingAddress"
                            name="overseaBillingAddress"
                            value={formValues.overseaBillingAddress ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="flex-grow-1 me-3">
                    <h4 className = "fw-bold text-center mt-2">Shipping Address</h4>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="shippingStreet">Street:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="shippingStreet"
                            name="shippingStreet"
                            value={formValues.shippingStreet ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="shippingCity">City:</label>
                        <select
                            className="form-control flex-grow-1 ms-3"
                            id="shippingCity"
                            name="shippingCity"
                            value={formValues.shippingCity ?? ""}
                            onChange={handleChange}
                        >
                            <option value="">Select</option>
                            {formValues.shippingState &&
                                cityStates[formValues.shippingState as keyof typeof cityStates]?.map(
                                    (s, i) => <option key={i}>{s}</option>
                                )}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold" htmlFor="shippingState">State:</label>
                        <select
                            className = "form-control flex-grow-1 ms-3"
                            id="shippingState"
                            name = "shippingState"
                            value = {formValues.shippingState ?? ""}
                            onChange = {handleChange}
                        >
                            <option value="">Select</option>
                            {states.map((s, i) => (
                                <option key={i}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="shippingZipCode">Zip Code:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="shippingZipCode"
                            name="shippingZipCode"
                            value={formValues.shippingZipCode ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="overseaShippingAddress">Oversea Address:</label>
                        <input
                            className="form-control flex-grow-1 ms-3"
                            id="overseaShippingAddress"
                            name="overseaShippingAddress"
                            value={formValues.overseaShippingAddress ?? ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="d-flex flex-column align-items-start mt-2">
                    <label className="fw-bold" htmlFor="background">Background:</label>
                    <textarea
                        className="form-control w-100 flex-grow-1"
                        id="background"
                        name="background"
                        value={formValues.background ?? ""}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <div className="my-3">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    {keyword}
                </button>
                <Link
                    to=".."
                    className="btn btn-outline-secondary float-end"
                >
                    Back
                </Link>
            </div>
        </Form>
    );
}
